import React, { useContext, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import RegionBanner from './RegionBanner'
import GlobalBanner from './Banner/GlobalBanner'
import MobileStickyCta from '@toasttab/shared/src/components/organisms//Navigation/MobileStickyCta'
import { useScrollPosition } from '@toasttab/shared/src/hooks/useScrollPosition'
import { usePrevious } from '@toasttab/shared/src/hooks/usePrevious'
import NavController from '@toasttab/shared/src/components/organisms/Navigation/NavController'
import { default as ContentfulNavController } from '@toasttab/shared/src/components/organisms/Navigation/Contentful/NavController'
import { OTLContext } from '@toasttab/shared/src/context/OTLContext'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { HeaderProps } from '@toasttab/shared/src/components/organisms/Header/Header.d'
import RegionSwitcher from './RegionSwitcher'
import RegionSwitcherInfoBanner from './RegionSwitcherInfoBanner'
import {
  useContentfulNav,
  useRegionSwitcherRedesign
} from '@toasttab/shared/src/utils/featureFlags'
import GlobalBannerOriginal from './Banner/GlobalBanner-Original'
import pricingTestBannerData from '@toasttab/shared/src/data/PricingTestBanner.json'
import { GlobalBannerData } from './Banner/GlobalBanner.d'

const Header = ({
  enabledMobileStickyCTA = false,
  headerContent,
  localizedEntries,
  siteId,
  siteMetadata
}: HeaderProps) => {
  const [readingProgress, setReadingProgress] = useState(0)
  const [readingMax, setReadingMax] = useState(0)
  const [isHidden, setIsHidden] = useState(false)

  const scrollPosition = useScrollPosition()
  const pageContext = usePageContext()
  const { headerHeight, isPricingTest, packageName, setHeaderHeight } =
    pageContext
  const headerStyle = pageContext?.headerStyle || 'Default'
  const otlParams = useContext(OTLContext)
  const OTLArticleUX: boolean = Boolean(otlParams.isArticle)
  const ref = useRef<any | null>(null)
  const activeRegionSwitcherInfoBanner =
    pageContext?.activeRegionSwitcherInfoBanner
  const [bannerMaxHeight, setBannerMaxHeight] = useState('0px')
  const bannerRef = useRef<HTMLDivElement>(null)

  const showTopBanner =
    scrollPosition <= 0 &&
    ['main', 'onTheLine'].includes(packageName) &&
    activeRegionSwitcherInfoBanner !== undefined

  useEffect(() => {
    if (bannerRef.current) {
      requestAnimationFrame(() => {
        if (showTopBanner) {
          setBannerMaxHeight(`${bannerRef.current!.scrollHeight}px`)
        } else {
          setBannerMaxHeight('0px')
        }
      })
    }
    // Re-set headerHeight when banner state changes
    setTimeout(() => {
      const headHeight =
        typeof document !== 'undefined'
          ? document.querySelector('.headerContent')?.clientHeight
          : 0
      if (headHeight > 0 && headHeight !== headerHeight) {
        setHeaderHeight(headHeight)
      }
    }, 100)
  }, [
    activeRegionSwitcherInfoBanner,
    headerHeight,
    setHeaderHeight,
    showTopBanner
  ])

  const topBannerClasses = classNames(
    'transition-all duration-1000 ease-in-out overflow-hidden'
  )

  // Store the previous scroll value so we can determine if the user is scrolling up or down to show or hide the nav
  const prevScroll = usePrevious(scrollPosition)

  useEffect(() => {
    if (OTLArticleUX) {
      const trackScrollPosition = () => {
        const winHeight: any = window.innerHeight,
          docHeight: any = document.documentElement.scrollHeight

        // Set the max scrollable area
        const max = docHeight - winHeight

        setReadingMax(max)
        setReadingProgress(scrollPosition)
      }

      document.addEventListener('scroll', function () {
        trackScrollPosition()
      })
    }
  }, [scrollPosition])

  useEffect(() => {
    if (!OTLArticleUX && siteId !== 1 && prevScroll) {
      const headHeight = ref.current.clientHeight
      // Slide header up if user is scrolling down
      if (!isHidden && scrollPosition > 50 && prevScroll < scrollPosition) {
        ref.current.style.top = `-${headHeight}px`
        setIsHidden(true)
        // Slide header back down into view if user is scrolling up
      } else if (
        isHidden &&
        (prevScroll > scrollPosition || scrollPosition === 0)
      ) {
        ref.current.style.top = '0'
        setIsHidden(false)
      }
    }
  }, [prevScroll, scrollPosition, isHidden])

  const headerClasses = classNames({
    'top-0 w-full z-50 bg-white transition-all': headerStyle !== 'Logo Only',
    fixed: headerStyle !== 'Logo Only' && !useContentfulNav,
    'sticky z-[100]': useContentfulNav,
    'shadow-[rgba(0,0,0,0.08)]': OTLArticleUX && scrollPosition > 100,
    'shadow-[rgba(0,0,0,0)]': !OTLArticleUX || scrollPosition <= 100,
    'shadow-nav duration-300': OTLArticleUX,
    'duration-1000': !OTLArticleUX
  })

  return (
    <>
      <header className={headerClasses} ref={ref}>
        <div className='headerContent'>
          {['main', 'onTheLine'].includes(packageName) &&
            (useRegionSwitcherRedesign ? (
              <>
                <div
                  className={topBannerClasses}
                  ref={bannerRef}
                  style={showTopBanner ? {} : { maxHeight: bannerMaxHeight }}
                >
                  <RegionSwitcherInfoBanner
                    localizedEntries={localizedEntries}
                    packageName={packageName}
                    siteId={siteId}
                  />
                  {/* 
                  If the region switcher info banner is false, show the global banner, otherwise show nothing -- 
                  this will make sure we are not seeing the flickering of the global banner, when both banners are active
                */}
                  {activeRegionSwitcherInfoBanner === false && <GlobalBanner />}
                </div>
                <RegionSwitcher
                  localizedEntries={localizedEntries}
                  packageName={packageName}
                  siteId={siteId}
                />
              </>
            ) : (
              <RegionBanner
                localizedEntries={localizedEntries}
                packageName={packageName}
                siteId={siteId}
              />
            ))}
          {/* MKE-3840: September Pricing Test
              We need to add this banner with custom data specifically for this test */}
          {isPricingTest ? (
            <GlobalBanner
              bannerData={pricingTestBannerData as GlobalBannerData}
            />
          ) : null}
          {useContentfulNav && headerContent ? (
            <ContentfulNavController
              headerContent={headerContent}
              localizedEntries={localizedEntries}
              packageName={packageName}
              siteId={siteId}
              siteMetadata={siteMetadata}
            />
          ) : (
            <NavController
              packageName={packageName}
              siteId={siteId}
              siteMetadata={siteMetadata}
            />
          )}
        </div>
        {['onTheLine', 'main'].includes(packageName) &&
          !useRegionSwitcherRedesign && <GlobalBannerOriginal />}
        {siteId === 1 &&
        !['support'].includes(packageName) &&
        enabledMobileStickyCTA ? (
          <MobileStickyCta />
        ) : null}
        {OTLArticleUX ? (
          <progress max={readingMax} value={readingProgress} />
        ) : null}
      </header>
    </>
  )
}

export default Header
